import {
  DETECTOR_KEYS,
  POST_FIELD_KEYS,
  SELECTOR_PACK_LOCALES,
  SELECTOR_PACK_PLATFORMS,
  SELECTOR_RULE_KEYS,
  selectorPackDocumentSchema,
} from '@midiautolistening/selector-pack';
import { z } from 'zod';

import {
  idSchema,
  isoDateTimeSchema,
  paginated,
  paginationQuerySchema,
  sortOrderSchema,
} from '../common';
import { selectorHealthOutcomeSchema, selectorPackVersionStatusSchema } from '../enums';
import { queryBooleanSchema } from './posts';

/**
 * `/selector-packs`, `/selector-health` and `/dom-snapshots` DTOs.
 *
 * A SelectorPack is *versioned data*, which is what lets an admin repair a
 * broken platform selector from the dashboard without rebuilding any agent.
 * The document schema is owned by `@midiautolistening/selector-pack` and reused
 * verbatim here: its enums are closed, so a pack carrying anything outside them
 * — above all a JavaScript source string — fails validation at publish time and
 * again at apply time on the agent. The interpreter is fixed code; the pack is
 * never executed.
 */

export const selectorPackPlatformSchema = z.enum(SELECTOR_PACK_PLATFORMS);
export const selectorPackLocaleSchema = z.enum(SELECTOR_PACK_LOCALES);

// ---------------------------------------------------------------------------
// Packs and versions
// ---------------------------------------------------------------------------

export const selectorPackSummarySchema = z.object({
  id: idSchema,
  name: z.string(),
  platform: selectorPackPlatformSchema,
  locale: selectorPackLocaleSchema,
  description: z.string().nullable(),
  /** Applied to every agent that has no more specific pack assigned. */
  isDefault: z.boolean(),
  activeVersionId: idSchema.nullable(),
  /** Monotonic per pack; this is the number agents compare against. */
  activeVersionNumber: z.number().int().min(0).nullable(),
  versionCount: z.number().int().min(0),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SelectorPackSummary = z.infer<typeof selectorPackSummarySchema>;

export const selectorPackVersionSchema = z.object({
  id: idSchema,
  packId: idSchema,
  versionNumber: z.number().int().min(1),
  status: selectorPackVersionStatusSchema,
  /** The declarative document itself, validated by the shared schema. */
  document: selectorPackDocumentSchema,
  /** Lowest agent adapter version able to interpret this document. */
  minAdapterVersion: z.number().int().min(1),
  changelog: z.string().nullable(),
  createdByUserId: idSchema.nullable(),
  createdAt: isoDateTimeSchema,
  publishedAt: isoDateTimeSchema.nullable(),
  rolledBackAt: isoDateTimeSchema.nullable(),
});
export type SelectorPackVersion = z.infer<typeof selectorPackVersionSchema>;

export const selectorPackListQuerySchema = paginationQuerySchema.extend({
  platform: selectorPackPlatformSchema.optional(),
  locale: selectorPackLocaleSchema.optional(),
  search: z.string().trim().max(120).optional(),
  onlyDefault: queryBooleanSchema.optional(),
});
export type SelectorPackListQuery = z.infer<typeof selectorPackListQuerySchema>;

export const selectorPackListResponseSchema = paginated(selectorPackSummarySchema);
export type SelectorPackListResponse = z.infer<typeof selectorPackListResponseSchema>;

export const createSelectorPackRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  platform: selectorPackPlatformSchema.default('facebook'),
  locale: selectorPackLocaleSchema,
  description: z.string().trim().max(2_000).optional(),
  isDefault: z.boolean().default(false),
  /** Creating a pack also creates version 1 as a DRAFT — nothing reaches an
   *  agent until it is published explicitly. */
  document: selectorPackDocumentSchema,
  changelog: z.string().trim().max(1_000).optional(),
});
export type CreateSelectorPackRequest = z.infer<typeof createSelectorPackRequestSchema>;

export const createSelectorPackResponseSchema = z.object({
  pack: selectorPackSummarySchema,
  version: selectorPackVersionSchema,
});
export type CreateSelectorPackResponse = z.infer<typeof createSelectorPackResponseSchema>;

export const updateSelectorPackRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2_000).nullable(),
    isDefault: z.boolean(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Cần ít nhất một trường để cập nhật',
  });
export type UpdateSelectorPackRequest = z.infer<typeof updateSelectorPackRequestSchema>;

export const selectorPackDetailResponseSchema = z.object({
  pack: selectorPackSummarySchema,
  /** Newest first. */
  versions: z.array(selectorPackVersionSchema),
  activeVersion: selectorPackVersionSchema.nullable(),
  /** Rule keys the adapter needs that the active document does not define. */
  missingRuleKeys: z.array(z.string()),
  assignedAgentCount: z.number().int().min(0),
});
export type SelectorPackDetailResponse = z.infer<typeof selectorPackDetailResponseSchema>;

// ---------------------------------------------------------------------------
// POST /selector-packs/:id/publish
// ---------------------------------------------------------------------------

/**
 * Publish either an existing draft (`versionId`) or a new document in one step.
 * Exactly one of the two, so "publish" can never mean "publish something other
 * than what the admin was looking at".
 */
export const publishSelectorPackRequestSchema = z
  .object({
    versionId: idSchema.optional(),
    document: selectorPackDocumentSchema.optional(),
    changelog: z.string().trim().max(1_000).optional(),
    minAdapterVersion: z.number().int().min(1).optional(),
  })
  .refine(
    (value) =>
      (value.versionId === undefined) !== (value.document === undefined),
    { message: 'Cung cấp đúng một trong hai: versionId hoặc document' },
  );
export type PublishSelectorPackRequest = z.infer<typeof publishSelectorPackRequestSchema>;

export const selectorPackPublishResultSchema = z.object({
  packId: idSchema,
  versionId: idSchema,
  versionNumber: z.number().int().min(1),
  status: selectorPackVersionStatusSchema,
  effectiveAt: isoDateTimeSchema,
  /** Agents notified over `selector_pack.updated`; offline ones pick the pack
   *  up on their next `agent.hello`, since application is idempotent by version. */
  notifiedAgentCount: z.number().int().min(0),
  pendingAgentCount: z.number().int().min(0),
});
export type SelectorPackPublishResult = z.infer<typeof selectorPackPublishResultSchema>;

// ---------------------------------------------------------------------------
// POST /selector-packs/:id/dump-coverage — đối chiếu một bản Dump DOM
// ---------------------------------------------------------------------------

/**
 * The admin uploads the agent's sanitised DOM dump; the server answers which
 * field rules still match it, per candidate. This is the "which selector died"
 * step of a recalibration, done without leaving the dashboard — the analysis
 * only ever PARSES the HTML (no script execution, no resource loading) and
 * nothing is persisted.
 */
export const dumpCoverageRequestSchema = z.object({
  /** The dump file's content, verbatim. The agent sanitises it before writing,
   *  and its writer caps the size — 8 MB is far above any real dump. */
  html: z.string().min(1).max(8_000_000),
  /** A draft document to try INSTEAD of the published one — the edit loop is
   *  "tweak → re-check" and must not require publishing to test. */
  document: selectorPackDocumentSchema.optional(),
});
export type DumpCoverageRequest = z.infer<typeof dumpCoverageRequestSchema>;

export const dumpCandidateCoverageSchema = z.object({
  strategy: z.string(),
  value: z.string(),
  /** Cards this candidate matched; null when the analyser could not evaluate
   *  the selector (the agent's real engine may still understand it). */
  hits: z.number().int().min(0).nullable(),
});
export type DumpCandidateCoverage = z.infer<typeof dumpCandidateCoverageSchema>;

export const dumpFieldCoverageSchema = z.object({
  fieldKey: z.string(),
  /** Cards with at least one matching candidate. */
  coveredCards: z.number().int().min(0),
  candidates: z.array(dumpCandidateCoverageSchema),
  /** Calibration context, e.g. "bề mặt tìm kiếm không render số này — 0 là đúng". */
  note: z.string().nullable(),
});
export type DumpFieldCoverage = z.infer<typeof dumpFieldCoverageSchema>;

export const dumpCoverageResponseSchema = z.object({
  /** Result cards found in the dump — the denominator of every ratio below. */
  cardCount: z.number().int().min(0),
  /** Which resultCard candidate found them, when any did. */
  cardCandidate: z.string().nullable(),
  /** Version analysed, null when a draft document was supplied. */
  packVersionNumber: z.number().int().min(1).nullable(),
  fields: z.array(dumpFieldCoverageSchema),
  warnings: z.array(z.string()),
});
export type DumpCoverageResponse = z.infer<typeof dumpCoverageResponseSchema>;

// ---------------------------------------------------------------------------
// POST /selector-packs/:id/rollback
// ---------------------------------------------------------------------------

export const rollbackSelectorPackRequestSchema = z
  .object({
    /** Target version, by id or by number. Defaults to the previously
     *  published version when neither is given. */
    versionId: idSchema.optional(),
    versionNumber: z.number().int().min(1).optional(),
    reason: z.string().trim().max(1_000).optional(),
  })
  .refine((value) => value.versionId === undefined || value.versionNumber === undefined, {
    message: 'Chỉ dùng versionId hoặc versionNumber, không dùng cả hai',
  });
export type RollbackSelectorPackRequest = z.infer<typeof rollbackSelectorPackRequestSchema>;

export const rollbackSelectorPackResponseSchema = selectorPackPublishResultSchema.extend({
  rolledBackFromVersionNumber: z.number().int().min(1),
});
export type RollbackSelectorPackResponse = z.infer<typeof rollbackSelectorPackResponseSchema>;

// ---------------------------------------------------------------------------
// GET /selector-health
// ---------------------------------------------------------------------------

/**
 * Everything an agent can report a health event for: locator rules, detectors
 * and extracted post fields. Closed for the same reason the pack vocabularies
 * are — an unknown key means the report is malformed, not that a new rule
 * appeared.
 */
export const SELECTOR_HEALTH_RULE_KEYS = [
  ...SELECTOR_RULE_KEYS,
  ...DETECTOR_KEYS,
  ...POST_FIELD_KEYS,
] as const;
export const selectorHealthRuleKeySchema = z.enum(SELECTOR_HEALTH_RULE_KEYS);
export type SelectorHealthRuleKey = (typeof SELECTOR_HEALTH_RULE_KEYS)[number];

export const selectorHealthQuerySchema = paginationQuerySchema.extend({
  packId: idSchema.optional(),
  agentId: idSchema.optional(),
  ruleKey: selectorHealthRuleKeySchema.optional(),
  locale: selectorPackLocaleSchema.optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  /** Default view is the failures — the point of the table is early warning. */
  onlyFailing: queryBooleanSchema.default(true),
  minFailureCount: z.coerce.number().int().min(0).optional(),
  sortOrder: sortOrderSchema,
});
export type SelectorHealthQuery = z.infer<typeof selectorHealthQuerySchema>;

export const selectorHealthItemSchema = z.object({
  ruleKey: selectorHealthRuleKeySchema,
  packId: idSchema.nullable(),
  packVersionNumber: z.number().int().min(0).nullable(),
  locale: selectorPackLocaleSchema,
  /** Worst outcome observed in the window. */
  worstOutcome: selectorHealthOutcomeSchema,
  successCount: z.number().int().min(0),
  fallbackCount: z.number().int().min(0),
  failureCount: z.number().int().min(0),
  /** failures / total, 0.0-1.0 — the column the table sorts on. */
  failureRate: z.number().min(0).max(1),
  affectedAgentCount: z.number().int().min(0),
  /** Path only; a full URL could carry ids from a user's session. */
  sampleUrlPath: z.string().nullable(),
  /**
   * Why the most recent failure in this window failed, in the interpreter's
   * words. Null when nothing failed, or when the reporting agent predates the
   * field.
   *
   * This is the difference between a number and a lead. "permalink 31% hỏng" is
   * a fact nobody can act on; "Card dropped: content String must contain at most
   * 20000 character(s)" names the file to open. Never carries page content — see
   * the note on `sampleMessage` in the agent→server contract.
   */
  sampleMessage: z.string().nullable(),
  adapterVersion: z.number().int().min(1).nullable(),
  firstSeenAt: isoDateTimeSchema,
  lastSeenAt: isoDateTimeSchema,
});
export type SelectorHealthItem = z.infer<typeof selectorHealthItemSchema>;

export const selectorHealthResponseSchema = paginated(selectorHealthItemSchema).extend({
  summary: z.object({
    windowFrom: isoDateTimeSchema,
    windowTo: isoDateTimeSchema,
    totalEvents: z.number().int().min(0),
    failingRuleCount: z.number().int().min(0),
    healthyRuleCount: z.number().int().min(0),
    reportingAgentCount: z.number().int().min(0),
  }),
});
export type SelectorHealthResponse = z.infer<typeof selectorHealthResponseSchema>;

// ---------------------------------------------------------------------------
// GET /dom-snapshots
// ---------------------------------------------------------------------------

/** Which surface a snapshot was captured from. */
export const DOM_SNAPSHOT_PAGE_KINDS = [
  'SEARCH_RESULTS',
  'POST_DETAIL',
  'COMMENT_BOX',
  'LOGIN',
  'CHECKPOINT',
  'OTHER',
] as const;
export const domSnapshotPageKindSchema = z.enum(DOM_SNAPSHOT_PAGE_KINDS);
export type DomSnapshotPageKind = (typeof DOM_SNAPSHOT_PAGE_KINDS)[number];

export const domSnapshotQuerySchema = paginationQuerySchema.extend({
  agentId: idSchema.optional(),
  projectId: idSchema.optional(),
  pageKind: domSnapshotPageKindSchema.optional(),
  locale: selectorPackLocaleSchema.optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
  sortOrder: sortOrderSchema,
});
export type DomSnapshotQuery = z.infer<typeof domSnapshotQuerySchema>;

/**
 * An uploaded DOM snapshot, listed with a short-lived signed URL so an admin
 * can read the markup and repair the pack.
 *
 * The agent sanitises before upload: no cookies, no tokens, no message bodies.
 * `sanitized` is a literal `true` because an unsanitised snapshot is not a
 * thing this endpoint is allowed to serve.
 */
export const domSnapshotItemSchema = z.object({
  id: idSchema,
  mediaObjectId: idSchema,
  agentId: idSchema,
  agentName: z.string(),
  projectId: idSchema.nullable(),
  pageKind: domSnapshotPageKindSchema,
  locale: selectorPackLocaleSchema,
  /** Path only, for the same reason as `sampleUrlPath` above. */
  urlPath: z.string().nullable(),
  sizeBytes: z.number().int().min(0),
  adapterVersion: z.number().int().min(1),
  selectorPackVersion: z.number().int().min(0),
  sanitized: z.literal(true),
  capturedAt: isoDateTimeSchema,
  /** Snapshots self-expire; the sweeper deletes the object at this instant. */
  expiresAt: isoDateTimeSchema.nullable(),
  signedUrl: z.string().url(),
  signedUrlExpiresAt: isoDateTimeSchema,
});
export type DomSnapshotItem = z.infer<typeof domSnapshotItemSchema>;

export const domSnapshotListResponseSchema = paginated(domSnapshotItemSchema);
export type DomSnapshotListResponse = z.infer<typeof domSnapshotListResponseSchema>;
